import { useEffect, useMemo, useRef, useState } from "react";
import { Check } from "lucide-react";
import { usePage, type SectionProps } from "./context";
import { Band, Head, cn, rv, str } from "./ui";
import { aiNav, aiNavAnchors } from "@/mocks/aiassist";
import { teamHints } from "./teams-meta";
import { useTeams, type HubTeam } from "./teams-store";
import { apiErrorText } from "@/lib/api";
import TeamModal from "./teammodal";
import { goAnchor } from "./app-nav";
import { t, tn, locale } from "@/lib/i18n";

/* Живой макет «Настройки команды» (Team settings): список команд рабочего пространства,
   поиск по настройкам и окно «Добавить команду» с названием, описанием и участниками. */

const initials = (name: string) =>
  name
    .split(/\s+/)
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase() || "•";

function TeamSettingsDemo({ title, subtitle }: SectionProps) {
  const { brand } = usePage();
  const { teams, people, create, remove: removeTeam, canManage } = useTeams();
  const [modal, setModal] = useState(false);
  const [query, setQuery] = useState("");
  const [toast, setToast] = useState("");
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(timer.current), []);

  const flash = (text: string) => {
    setToast(text);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setToast(""), 4600);
  };

  const onNav = (id: string) => {
    if (id === "team-settings") return;
    if (id === "ai-assist") {
      goAnchor("#ai-assist");
      return;
    }
    const anchor = aiNavAnchors[id];
    if (anchor) {
      goAnchor(anchor);
      return;
    }
    flash(t("Раздел «{label}» в демо не открывается", { label: aiNav.flatMap((g) => g.items).find((x) => x.id === id)?.label ?? "" }));
  };

  const save = async (team: { name: string; description: string; members: string[] }) => {
    try {
      await create(team);
      setModal(false);
      flash(t("Команда «{name}» создана — можно назначать ей диалоги и контакты", { name: team.name }));
    } catch (err) {
      flash(apiErrorText(err));
    }
  };

  const remove = async (team: HubTeam) => {
    try {
      await removeTeam(team.id);
      flash(t("Команда «{name}» удалена", { name: team.name }));
    } catch (err) {
      flash(apiErrorText(err));
    }
  };

  const openModal = () => (canManage ? setModal(true) : flash(t("Команды создают владелец, администраторы и менеджеры")));

  const found = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return teams;
    return teams.filter((team) => `${team.name} ${team.description}`.toLowerCase().includes(q));
  }, [teams, query]);

  const membersOf = (team: HubTeam) => people.filter((m) => team.members.includes(m.id));

  return (
    <Band id="team-settings" tone="muted">
      <Head title={title} subtitle={subtitle} />
      <div {...rv()} className="flash-card relative overflow-hidden rounded-card border border-border bg-card !p-0">
        {/* полоска «окна приложения» */}
        <div className="flex flex-wrap items-center gap-2.5 border-b border-border px-4 py-3">
          <span className="flex gap-1.5" aria-hidden>
            <span className="size-2.5 rounded-full bg-foreground/20" />
            <span className="size-2.5 rounded-full bg-foreground/20" />
            <span className="size-2.5 rounded-full bg-foreground/20" />
          </span>
          <span className="flash-mono ms-1 truncate text-[11px] text-muted-foreground">
            {str(brand) || "Hub"} / {t("настройки → настройки команды")}
          </span>
          <span className="ms-auto inline-flex items-center gap-2 whitespace-nowrap text-[11px] text-muted-foreground">
            <span className="size-1.5 rounded-full bg-primary" aria-hidden />
            {t("Команд: {n}", { n: teams.length })}
          </span>
        </div>

        <div className="grid lg:grid-cols-[14.5rem_minmax(0,1fr)]">
          {/* ── настройки ── */}
          <div className="hidden flex-col gap-4 border-border p-3 lg:flex lg:border-e">
            {aiNav.map((g) => (
              <div key={g.group} className="flex flex-col gap-0.5">
                <p className="px-2 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{g.group}</p>
                {g.items.map((it) => {
                  const active = it.id === "team-settings";
                  return (
                    <button
                      key={it.id}
                      type="button"
                      onClick={() => onNav(it.id)}
                      aria-current={active ? "page" : undefined}
                      className={cn(
                        "flex cursor-pointer items-center gap-2.5 rounded-btn px-2.5 py-2 text-start text-[12.5px] font-medium transition",
                        active ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground",
                      )}
                    >
                      <i className={cn(it.icon, "text-base leading-none")} aria-hidden />
                      <span className="min-w-0 flex-1 truncate">{it.label}</span>
                      {it.badge ? (
                        <span className="rounded-full bg-amber-400/20 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-600">{it.badge}</span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            ))}

            <div className="mt-auto rounded-btn border border-border bg-background/60 p-3">
              <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-primary">
                <i className="ri-shield-check-line text-sm leading-none" aria-hidden />
                {t("Доступы")}
              </p>
              <p className="mt-1.5 text-[11px] leading-relaxed text-muted-foreground">
                {t("Команды создают владелец, администраторы и менеджеры. Роль пользователя при этом не меняется.")}
              </p>
            </div>
          </div>

          {/* ── содержимое окна ── */}
          <div className="min-w-0">
            <div className="flex gap-1.5 overflow-x-auto border-b border-border px-3.5 py-2 lg:hidden">
              {aiNav.flatMap((g) => g.items).map((it) => (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => onNav(it.id)}
                  className={cn(
                    "inline-flex flex-none cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] font-semibold transition",
                    it.id === "team-settings" ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:text-foreground",
                  )}
                >
                  <i className={it.icon} aria-hidden />
                  {it.label}
                </button>
              ))}
            </div>

            <div className="p-3.5 md:p-5">
              <div className="flex flex-wrap items-start gap-2.5">
                <span className="grid size-8 flex-none place-items-center rounded-btn bg-primary/10 text-primary" aria-hidden>
                  <i className="ri-team-line text-base leading-none" />
                </span>
                <div className="min-w-0 flex-1">
                  <h3 className="text-base font-semibold leading-snug">{t("Настройки команды")}</h3>
                  <p className="mt-1 text-[12px] leading-relaxed text-muted-foreground">
                    {t("Создавайте команды и управляйте ими, чтобы группировать пользователей рабочего пространства.")}
                  </p>
                </div>
              </div>

              <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center">
                <button
                  type="button"
                  onClick={openModal}
                  className="inline-flex h-9 flex-none cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-btn bg-primary px-3.5 text-[12.5px] font-semibold text-primary-foreground transition hover:brightness-110"
                >
                  <i className="ri-add-line text-sm leading-none" aria-hidden />
                  {t("Добавить команду")}
                </button>
                <div className="relative sm:ms-auto sm:w-64">
                  <i className="ri-search-line pointer-events-none absolute start-2.5 top-1/2 -translate-y-1/2 text-sm leading-none text-muted-foreground" aria-hidden />
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={t("Поиск по командам")}
                    aria-label={t("Поиск по командам")}
                    className="h-9 w-full rounded-btn border border-border bg-background ps-8 pe-3 text-[12px] placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                </div>
              </div>

              {teams.length === 0 ? (
                <div className="mt-4 rounded-btn border border-dashed border-border bg-background/40 px-4 py-10 text-center">
                  <span className="relative mx-auto grid size-20 place-items-center rounded-full bg-foreground/[.05] text-muted-foreground" aria-hidden>
                    <i className="ri-team-line text-3xl leading-none" />
                    <span className="absolute -end-1 -bottom-1 grid size-8 place-items-center rounded-full border border-border bg-card text-primary">
                      <i className="ri-user-add-line text-sm leading-none" />
                    </span>
                  </span>
                  <p className="mt-4 text-[14px] font-semibold">{t("Пока нет ни одной команды")}</p>
                  <p className="mx-auto mt-1.5 max-w-sm text-[12px] leading-relaxed text-muted-foreground">
                    {t("Создайте команду, чтобы разом назначать диалоги и контакты группе сотрудников, а не каждому по отдельности.")}
                  </p>
                  <button
                    type="button"
                    onClick={openModal}
                    className="mt-4 inline-flex h-8 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-btn border border-border px-3 text-[11.5px] font-semibold transition hover:border-primary/50 hover:text-primary"
                  >
                    <i className="ri-add-line text-[13px] leading-none" aria-hidden />
                    {t("Создать команду")}
                  </button>
                </div>
              ) : found.length === 0 ? (
                <div className="mt-4 rounded-btn border border-dashed border-border bg-background/40 px-4 py-10 text-center">
                  <p className="text-[13px] font-semibold">{t("По запросу «{q}» ничего не нашли", { q: query.trim() })}</p>
                  <p className="mt-1 text-[12px] text-muted-foreground">{t("Попробуйте другое название или очистите поиск.")}</p>
                  <button
                    type="button"
                    onClick={() => setQuery("")}
                    className="mt-3 inline-flex h-8 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-btn border border-border px-3 text-[11.5px] font-semibold transition hover:border-primary/50 hover:text-primary"
                  >
                    <i className="ri-refresh-line text-[13px] leading-none" aria-hidden />
                    {t("Сбросить поиск")}
                  </button>
                </div>
              ) : (
                <ul className="mt-4 grid gap-2.5">
                  {found.map((team) => {
                    const crew = membersOf(team);
                    return (
                      <li
                        key={team.id}
                        className="flex flex-wrap items-start gap-3 rounded-btn border border-border bg-background/50 p-3 transition hover:border-primary/40"
                      >
                        <span className="grid size-9 flex-none place-items-center rounded-btn bg-primary/10 text-primary" aria-hidden>
                          <i className="ri-team-line text-base leading-none" />
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="flex flex-wrap items-center gap-2 text-[13px] font-semibold leading-snug">
                            {team.name}
                            <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary">
                              {tn(crew.length, "участник", "участника", "участников")}
                            </span>
                          </p>
                          <p className="mt-1 text-[11.5px] leading-relaxed text-muted-foreground">
                            {team.description || t("Без описания")}
                          </p>
                          {crew.length > 0 ? (
                            <div className="mt-2 flex flex-wrap items-center gap-1.5">
                              {crew.slice(0, 5).map((m) => (
                                <span
                                  key={m.id}
                                  title={`${m.name} · ${m.role}`}
                                  className="inline-flex items-center gap-1 rounded-full bg-foreground/[.05] py-0.5 pe-2 ps-0.5 text-[10.5px] font-medium text-foreground/75"
                                >
                                  <span className="grid size-5 place-items-center rounded-full bg-primary/15 text-[8px] font-bold text-primary" aria-hidden>
                                    {initials(m.name)}
                                  </span>
                                  {m.name}
                                </span>
                              ))}
                              {crew.length > 5 ? (
                                <span className="text-[10.5px] text-muted-foreground">+{crew.length - 5}</span>
                              ) : null}
                            </div>
                          ) : (
                            <p className="mt-1.5 text-[10.5px] text-muted-foreground">{t("Участники ещё не добавлены")}</p>
                          )}
                        </div>
                        <div className="flex flex-none items-center gap-1">
                          <span className="hidden text-[10.5px] text-muted-foreground sm:inline">
                            {new Date(team.createdAt).toLocaleDateString(locale)}
                          </span>
                          <button
                            type="button"
                            onClick={() => void remove(team)}
                            aria-label={t("Удалить команду {name}", { name: team.name })}
                            title={t("Удалить команду")}
                            className="grid size-7 cursor-pointer place-items-center rounded-btn text-muted-foreground transition hover:bg-red-500/10 hover:text-red-600"
                          >
                            <i className="ri-delete-bin-line text-[13px] leading-none" aria-hidden />
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}

              <div className="mt-4 grid gap-2 border-t border-border/60 pt-3 sm:grid-cols-3">
                {teamHints.map((h) => (
                  <div key={h.title} className="rounded-btn border border-border bg-background/40 p-2.5">
                    <p className="flex items-center gap-1.5 text-[11.5px] font-semibold">
                      <i className={cn(h.icon, "text-[13px] leading-none text-primary")} aria-hidden />
                      {h.title}
                    </p>
                    <p className="mt-1 text-[10.5px] leading-relaxed text-muted-foreground">{h.text}</p>
                  </div>
                ))}
              </div>

              <p className="mt-3 text-[11px] text-muted-foreground">
                {t("Команды общие для всего рабочего пространства: их видят все, меняют владелец, администраторы и менеджеры.")}
              </p>
            </div>

            {toast ? (
              <div
                role="status"
                className="pointer-events-none absolute inset-x-3 bottom-3 z-40 mx-auto flex max-w-md items-start gap-2 rounded-btn border border-primary/40 bg-background/95 px-3.5 py-2.5 text-[12px] text-foreground shadow-lift backdrop-blur"
              >
                <Check className="mt-0.5 size-3.5 flex-none text-primary" strokeWidth={3} aria-hidden />
                {toast}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {modal ? <TeamModal people={people} onClose={() => setModal(false)} onSave={(team) => void save(team)} /> : null}
    </Band>
  );
}

export default TeamSettingsDemo;